import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useFocusEffect } from '@react-navigation/native';
import * as Haptics from 'expo-haptics';
import { Svg, Path } from 'react-native-svg';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { apiService } from '@/services/apiService';
import { Skeleton } from '@/components/Skeleton';
import TrailListItem from '@/components/TrailListItem';
import TrailStartBottomSheet from '@/components/TrailStartBottomSheet';
import { trailRecentUsersService, TrailRecentUser } from '@/services/trailRecentUsersService';
import type { EloTrail, TrailProgress } from '@/components/TrailCard';

interface EnrichedTrail extends EloTrail {
  user_progress: TrailProgress & { hasStarted: boolean; startedCount: number };
}

const BackArrowIcon = () => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path d="M15.75 19L8.75 12L15.75 5" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
  </Svg>
);

const BookmarkIcon = () => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19.2288 6.58638C19.2288 4.02357 17.4786 3 14.9586 3H9.04185C6.5997 3 4.76953 3.95643 4.76953 6.41514V20.1165C4.76856 20.604 5.16358 21 5.65202 21C5.80381 21 5.95268 20.9612 6.08499 20.8871L12.028 17.5557L17.9154 20.8814C18.3405 21.1206 18.8796 20.9708 19.119 20.5457C19.1939 20.4134 19.2337 20.2644 19.2337 20.1117L19.2288 6.58638Z"
      stroke="#151515"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

export default function TrailCategoryPage() {
  const router = useRouter();
  const { t } = useLanguage();
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const { session, sessionReady, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [trails, setTrails] = useState<EnrichedTrail[]>([]);
  const [categoryDescription, setCategoryDescription] = useState('');
  const [startSheetTrail, setStartSheetTrail] = useState<EnrichedTrail | null>(null);
  const [sheetRecentUsers, setSheetRecentUsers] = useState<TrailRecentUser[]>([]);

  const decodedSlug = slug ? decodeURIComponent(slug).trim() : '';

  const categoryDisplayName = decodedSlug
    ? decodedSlug.charAt(0).toUpperCase() + decodedSlug.slice(1)
    : '';

  useFocusEffect(
    useCallback(() => {
      // PV2-16 (CL-P03): session?.access_token removed from deps — token rotation
      // must not trigger a data refetch. Only user identity and slug changes matter.
      if (user && sessionReady && decodedSlug) {
        loadTrails();
      }
    }, [user, sessionReady, decodedSlug])
  );

  const loadTrails = async () => {
    try {
      setLoading(true);
      const [trailsResponse, categoriesResponse] = await Promise.all([
        apiService.get(`/api/trails?category=${encodeURIComponent(decodedSlug)}`),
        apiService.get('/api/trail/categories'),
      ]);
      if (trailsResponse.success && trailsResponse.data?.trails) {
        setTrails(trailsResponse.data.trails);
      }
      if (categoriesResponse.success && Array.isArray(categoriesResponse.data?.categories)) {
        const match = categoriesResponse.data.categories.find(
          (c: any) =>
            (typeof c === 'object' ? c.name : c).toLowerCase() === decodedSlug.toLowerCase()
        );
        if (match && typeof match === 'object' && match.description) {
          setCategoryDescription(match.description);
        }
      }
    } catch (error) {
      console.error('Error loading category trails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTrailPress = (trail: EnrichedTrail) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (trail.user_progress?.hasStarted) {
      router.push(`/trail/${trail.slug}` as any);
    } else {
      const cached = trailRecentUsersService.getCachedUsers(trail.slug);
      setSheetRecentUsers(cached ?? []);
      setStartSheetTrail(trail);
      if (!cached) {
        trailRecentUsersService.getRecentUsers(trail.slug).then((users) => {
          setSheetRecentUsers(users);
        });
      }
    }
  };

  const handleStartTrail = (trail: EnrichedTrail) => {
    setStartSheetTrail(null);
    router.push(`/trail/${trail.slug}` as any);
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} style={styles.headerBtn}>
            <BackArrowIcon />
          </TouchableOpacity>
          <Skeleton width={100} height={14} />
          <View style={styles.headerBtn} />
        </View>

        <View style={styles.divider} />

        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          <View style={styles.infoSection}>
            <Skeleton width={24} height={24} borderRadius={4} />
            <Skeleton width="60%" height={28} borderRadius={4} />
            <Skeleton width="100%" height={16} borderRadius={4} />
            <Skeleton width="90%" height={16} borderRadius={4} />
          </View>

          <View style={styles.labelRow}>
            <Skeleton width={60} height={14} borderRadius={4} />
          </View>

          <View style={styles.trailList}>
            {[1, 2, 3, 4].map((i) => (
              <View key={i} style={styles.skeletonRow}>
                <Skeleton width={64} height={64} borderRadius={12} />
                <View style={styles.skeletonInfo}>
                  <Skeleton width="80%" height={18} borderRadius={4} />
                  <Skeleton width="50%" height={12} borderRadius={4} />
                </View>
                <Skeleton width={24} height={24} borderRadius={4} />
              </View>
            ))}
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.headerBtn}>
          <BackArrowIcon />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{categoryDisplayName}</Text>
        <View style={styles.headerBtn} />
      </View>

      <View style={styles.divider} />

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.infoSection}>
          <BookmarkIcon />
          <Text style={styles.categoryTitle}>{categoryDisplayName}</Text>
          {categoryDescription ? (
            <Text style={styles.categoryDescription}>{categoryDescription}</Text>
          ) : null}
        </View>

        <View style={styles.labelRow}>
          <Text style={styles.labelText}>{t('trail.category.trails')}</Text>
        </View>

        {trails.length > 0 ? (
          <View style={styles.trailList}>
            {trails.map((trail) => (
              <TrailListItem
                key={trail.slug}
                trail={trail}
                progress={trail.user_progress}
                onPress={() => handleTrailPress(trail)}
              />
            ))}
          </View>
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyTitle}>{t('trail.category.empty')}</Text>
            <Text style={styles.emptySubtitle}>{t('trail.category.emptySubtitle')}</Text>
          </View>
        )}
      </ScrollView>

      <TrailStartBottomSheet
        trail={startSheetTrail}
        isVisible={startSheetTrail !== null}
        onClose={() => setStartSheetTrail(null)}
        onStart={handleStartTrail}
        recentUsers={sheetRecentUsers}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    height: 56,
    backgroundColor: '#F9F8F3',
  },
  headerBtn: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: '#151515',
    lineHeight: 14,
    flex: 1,
    textAlign: 'center',
    paddingHorizontal: 8,
  },
  divider: {
    height: 1,
    backgroundColor: '#E7EAED',
  },

  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 48,
  },

  infoSection: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 24,
    gap: 12,
  },
  categoryTitle: {
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    color: '#151515',
    lineHeight: 28.8,
  },
  categoryDescription: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#151515',
    lineHeight: 19.6,
  },

  labelRow: {
    paddingHorizontal: 24,
    paddingBottom: 16,
  },
  labelText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: '#151515',
    lineHeight: 19.6,
  },

  trailList: {
    paddingHorizontal: 24,
    gap: 16,
  },

  skeletonRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 24,
  },
  skeletonInfo: {
    flex: 1,
    gap: 8,
  },

  emptyContainer: {
    paddingHorizontal: 24,
    paddingTop: 48,
    alignItems: 'center',
    gap: 8,
  },
  emptyTitle: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 14,
    color: '#151515',
    textAlign: 'center',
  },
  emptySubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#7E7D75',
    textAlign: 'center',
    lineHeight: 19.6,
  },
});
